/**
 * Cross-org user search — find a user by email or UID, see which org(s)
 * they belong to and their account state.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';

export default function UsersSearch() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleSearch(e) {
    e?.preventDefault?.();
    if (q.trim().length < 2) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/ops_admin/users/search?q=${encodeURIComponent(q.trim())}`);
      setResults(r);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="ops-users-search">
      <div className="ops-page-header">
        <h1>Find Users</h1>
      </div>

      <form className="ops-filters" onSubmit={handleSearch}>
        <input
          className="form-input search-input"
          placeholder="Email, sign-in username, or UID (min 2 chars)..."
          value={q}
          onChange={e => setQ(e.target.value)}
          autoFocus
        />
        <button className="btn btn-primary" type="submit" disabled={loading || q.trim().length < 2}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && <p className="error-text">{error}</p>}

      {results && (
        <>
          <h2>Members ({results.members.length})</h2>
          {results.members.length === 0 && (
            <p className="empty-row">No members matched.</p>
          )}
          {results.members.length > 0 && (
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Org</th>
                  <th>Role</th>
                  <th>Org State</th>
                  <th>Joined</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {results.members.map(m => (
                  <tr key={m.member_id}>
                    <td>{m.email}<div className="org-slug mono">{m.user_id}</div></td>
                    <td>{m.org_name}</td>
                    <td>{m.role}</td>
                    <td><span className={`badge badge-${m.org_state}`}>{m.org_state}</span></td>
                    <td>{m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <Link to={`/ops_admin/orgs/${m.org_id}/members`} className="btn btn-sm btn-ghost">
                        Open in org
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {results.firebase_only && (
            <div className="firebase-only-card">
              <h3>Found at the identity provider (no org membership)</h3>
              <p>
                This email has an identity account but isn't a member of any org. They likely
                haven't been invited yet.
              </p>
              <div className="info-grid">
                <div className="info-item"><label>UID</label><span className="mono">{results.firebase_only.uid}</span></div>
                {results.firebase_only.identity_email && (
                  <div className="info-item"><label>Sign-in username</label><span className="mono">{results.firebase_only.identity_email}</span></div>
                )}
                <div className="info-item"><label>Status</label><span>{results.firebase_only.disabled ? 'Disabled' : 'Enabled'}</span></div>
                <div className="info-item"><label>Providers</label><span>{(results.firebase_only.providers || []).join(', ') || '—'}</span></div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
